import React from "react";
import { animate } from "motion";

/**
 * Fade a section in as it enters the viewport.
 * One IntersectionObserver per section ref — fires once, then disconnects.
 */
export function useFadeInOnScroll(ref) {
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (prefersReduced) return;

    node.style.opacity = "0";
    node.style.transform = "translateY(20px)";

    if (typeof IntersectionObserver !== "function") {
      node.style.opacity = "1";
      node.style.transform = "none";
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          animate(
            node,
            { opacity: 1, transform: "translateY(0px)" },
            { duration: 0.5, easing: [0.25, 0.1, 0.25, 1] }
          );
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
}

/**
 * Homepage-specific sx tokens.
 * Deliberately sparse: only what the 6 sections need.
 * One subtle amber glow. No SVG waves, no scan-lines.
 */
export function getTemplateSx() {
  return {
    pageRoot: {
      minHeight: "100vh",
      color: "#ffffff",
      background:
        "radial-gradient(ellipse at 18% 0%, rgba(232, 163, 61, 0.09), transparent 46%), linear-gradient(180deg, #080808 0%, #0d0d0d 55%, #111111 100%)",
      "& *, & *:before, & *:after": { boxSizing: "border-box" },
    },
    hero: {
      minHeight: { xs: "100svh", md: "100vh" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    },
    heroInner: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      py: { xs: 12, md: 0 },
      mx: "auto",
      maxWidth: { xs: "100%", md: "56ch" },
    },
    heroLogo: {
      display: "block",
      height: { xs: 28, md: 34 },
      width: "auto",
      mx: "auto",
      mb: { xs: 3, md: 4 },
      opacity: 0.9,
    },
    h1: {
      fontSize: { xs: "2.35rem", sm: "3.4rem", md: "4.6rem" },
      fontWeight: 800,
      lineHeight: 0.97,
      letterSpacing: "-0.03em",
      mb: 2.5,
      textAlign: "center",
    },
    h1Line: {
      display: "block",
      textAlign: "center",
      whiteSpace: "nowrap",
    },
    sub: {
      fontSize: { xs: "1rem", md: "1.15rem" },
      lineHeight: 1.45,
      color: "rgba(255,255,255,0.65)",
    },
    subLine: {
      display: "block",
    },
    ctaRow: {
      mt: 4,
      display: "flex",
      justifyContent: "center",
    },
    trustStrip: {
      borderTop: "1px solid rgba(255,255,255,0.06)",
      py: { xs: 4, md: 5 },
    },
    trustStripInner: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "flex-start",
      gap: { xs: 5, md: 9 },
    },
    trustStat: {
      textAlign: "center",
    },
    trustValue: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: { xs: "2rem", md: "2.4rem" },
      fontWeight: 800,
      color: "#E8A33D",
      lineHeight: 1,
    },
    trustLabel: {
      mt: 0.75,
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      fontSize: "0.8rem",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: "rgba(255,255,255,0.5)",
    },
    trustLabelLink: {
      color: "rgba(255,255,255,0.5)",
      textDecoration: "none",
      transition: "color 0.15s ease-in-out",
      "&:hover": { color: "rgba(255,255,255,0.8)" },
    },
    liveDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      backgroundColor: "#3BA55D",
      boxShadow: "0 0 0 3px rgba(59,165,93,0.18)",
      flexShrink: 0,
    },
    section: {
      py: { xs: 8, md: 11 },
      borderTop: "1px solid rgba(255,255,255,0.06)",
      textAlign: "center",
    },
    sectionTitle: {
      fontSize: { xs: "1.6rem", md: "2.1rem" },
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.15,
      mb: 2,
    },
    sectionCopy: {
      fontSize: { xs: "0.98rem", md: "1.06rem" },
      lineHeight: 1.75,
      color: "rgba(255,255,255,0.62)",
      mx: "auto",
      maxWidth: "58ch",
    },
    threeGrid: {
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
      gap: { xs: 5, md: 6 },
    },
    thingNumber: {
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: "0.67rem",
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      color: "#E8A33D",
      mb: 1.5,
      display: "block",
    },
    thingTitle: {
      fontSize: "1.1rem",
      fontWeight: 700,
      lineHeight: 1.3,
      mb: 0.75,
    },
    thingBody: {
      fontSize: "0.94rem",
      lineHeight: 1.65,
      color: "rgba(255,255,255,0.56)",
    },
    faqGrid: {
      display: "grid",
      gap: 0,
      mt: 3,
    },
    faqItem: {
      py: 2.5,
      borderTop: "1px solid rgba(255,255,255,0.06)",
      "&:last-of-type": {
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      },
    },
    faqQuestion: {
      fontSize: "1rem",
      fontWeight: 700,
      mb: 0.75,
    },
    faqAnswer: {
      fontSize: "0.94rem",
      lineHeight: 1.7,
      color: "rgba(255,255,255,0.58)",
    },
    faqLink: {
      mt: 3,
    },
    callout: {
      py: { xs: 8, md: 12 },
      borderTop: "1px solid rgba(255,255,255,0.06)",
      textAlign: "center",
    },
    calloutTitle: {
      fontSize: { xs: "1.4rem", md: "1.75rem" },
      fontWeight: 700,
      letterSpacing: "-0.02em",
      mb: 3,
    },
    secondaryButton: {
      minHeight: 44,
      px: 2,
      borderRadius: "999px",
      textTransform: "none",
      fontWeight: 600,
      borderColor: "rgba(255,255,255,0.18)",
      color: "rgba(255,255,255,0.72)",
      fontSize: "0.9rem",
      transition: "border-color 0.15s ease-in-out, color 0.15s ease-in-out",
      "&:hover": {
        borderColor: "rgba(255,255,255,0.34)",
        backgroundColor: "rgba(255,255,255,0.04)",
        color: "#ffffff",
      },
    },
  };
}
